// What EXW / FOB / CIF / DDP actually mean, said to the TRADER in plain
// language — and, since 2026-09-21, whether the offer reaches where they asked.
//
// ── Why this exists ─────────────────────────────────────────────────────────
// The trader's offer card rendered «أساس السعر: EXW» and nothing else. A Saudi
// trader does not read "EXW" as "everything after the factory door is your
// cost" — they read the number as the whole price. Comparing an EXW quote with
// a CIF one then looks like comparing two prices, when it is comparing two
// different deals.
//
// ⚠️ THESE ARE NOT THE SUPPLIER'S STRINGS AND MUST NOT BE MERGED WITH THEM.
// `src/components/quote/quoteStrings.js` carries a `priceBases` map describing
// the SAME three terms in the second person TO THE FACTORY — «المشتري يرتّب كل
// شيء من باب مصنعك», "you deliver to the port, buyer pays freight". This file
// is the other side of that same agreement: what it costs the reader. Keeping
// two sets is the point, not duplication — one says "you ship it", the other
// says "you pay for shipping", and they describe one deal.
//
// Keep them deliberately in step: if the substance of one changes, the other
// is wrong, and a trader and a factory will have read different promises.
//
// ── DDP, added 2026-09-21 ───────────────────────────────────────────────────
// Door delivery had no representation before migration 20260921122524: a
// supplier who trucks to Riyadh had to call it CIF or write it in the note, so
// the trader could not tell "to the port" from "to my door" — the single
// distinction this whole feature exists to make visible.
//
// ── `line` vs `gloss` vs `full` ─────────────────────────────────────────────
// `line` is the sentence under the SUPPLIER'S NAME answering "who delivers to
// me?" — owner-approved wording, 2026-09-21, in Saudi register. `gloss` is the
// 2–4 word inline form. `full` is the explainer behind ⓘ. Three lengths of one
// fact; changing one without the others makes the card contradict itself.
//
// ── The unspecified case is not an edge case ────────────────────────────────
// 3 of 5 offers in production carry no price_basis at all, so "not specified"
// is what a trader mostly meets. It gets a line too (owner, 2026-09-20),
// because an offer with no basis sitting beside one quoting FOB reads as the
// cheaper of the two when the two are not comparable. Neutral wording, no
// alarm: offers predating the field (2026-09-19) are not at fault.

import { cityLabel } from './requestFormOptions';
const norm = (v) => String(v ?? '').trim().toUpperCase();

/**
 * Trader-facing copy, keyed by the value stored in offers.price_basis.
 *
 * `gloss` is 2–4 words and renders INLINE on the existing row, so the card's
 * layout does not change. `full` is one sentence, revealed on demand.
 */
export const PRICE_BASIS_EXPLAINERS = {
  EXW: {
    ar: {
      gloss: 'تسليم باب المصنع',
      line: 'تستلمها من المصنع في الصين',
      full: 'السعر عند باب المصنع في الصين. الشحن والتخليص الجمركي والتوصيل كلها عليك.',
    },
    en: {
      gloss: 'At the factory door',
      line: 'You collect it from the factory in China',
      full: 'The price at the factory door in China. Shipping, customs and delivery are all on you.',
    },
    zh: {
      gloss: '工厂交货',
      line: '您自行到中国工厂提货',
      full: '中国工厂交货价。运输、清关和配送均由您承担。',
    },
  },
  FOB: {
    ar: {
      gloss: 'تسليم ميناء الصين',
      line: 'يسلّمها في ميناء الصين، والشحن وما بعده عليك',
      full: 'المصنع يوصّل البضاعة إلى الميناء في الصين ويحمّلها. الشحن البحري وكل ما بعده عليك.',
    },
    en: {
      gloss: 'At the Chinese port',
      line: 'They hand it over at the Chinese port; freight onward is on you',
      full: 'The factory delivers to the port in China and loads it. Sea freight and everything after is on you.',
    },
    zh: {
      gloss: '中国装运港交货',
      line: '在中国港口交货，之后的运费由您承担',
      full: '工厂负责送到中国港口并装船。海运及之后的费用均由您承担。',
    },
  },
  CIF: {
    ar: {
      gloss: 'إلى الميناء السعودي',
      line: 'يوصّلها لين الميناء السعودي، والتخليص والتوصيل عليك',
      full: 'الشحن البحري والتأمين حتى ميناء الوصول مشمولان. التخليص الجمركي والتوصيل الداخلي عليك.',
    },
    en: {
      gloss: 'To the Saudi port',
      line: 'They deliver to the Saudi port; clearing and delivery are on you',
      full: 'Sea freight and insurance to the arrival port are included. Customs and local delivery are on you.',
    },
    zh: {
      gloss: '到沙特港口',
      line: '送到沙特港口，清关和境内配送由您承担',
      full: '含海运和保险至目的港。清关和境内配送由您承担。',
    },
  },
  DDP: {
    ar: {
      gloss: 'إلى بابك',
      // {city} is substituted by deliveryLine(); with no city it reads
      // «يوصّلها لين بابك» on its own, which is still a complete sentence.
      line: 'يوصّلها لين بابك{city}',
      full: 'السعر يشمل كل شيء حتى عنوانك: الشحن والتأمين والرسوم الجمركية والتوصيل الداخلي.',
    },
    en: {
      gloss: 'To your door',
      line: 'They deliver to your door{city}',
      full: 'The price covers everything to your address: freight, insurance, duties and local delivery.',
    },
    zh: {
      gloss: '送到您门口',
      line: '直接送到您的地址{city}',
      full: '价格涵盖至您地址的全部费用：运输、保险、关税和境内配送。',
    },
  },
};

/** The no-basis case. */
export const PRICE_BASIS_UNSPECIFIED = {
  ar: { gloss: '', line: 'المورد ما حدّد وين يوصّل', full: 'المورّد لم يوضّح ما يشمله السعر، فلا يمكن مقارنته بعرض وضّح ذلك.' },
  en: { gloss: '', line: "The supplier didn't say where they deliver", full: "The supplier didn't state what's included, so this price can't be compared with one that did." },
  zh: { gloss: '', line: '供应商未说明送到哪里', full: '供应商未说明价格包含哪些费用，因此无法与已说明的报价比较。' },
};

/**
 * THE MISMATCH FLAG. Raised only when compareDelivery() says 'short' — the
 * offer stops before where the trader asked. Never for 'exceeds': a supplier
 * who carries it further than asked has not done anything wrong, and an alarm
 * there would train the trader to ignore the one that matters.
 *
 * The {ask} slot names what THEY asked for, in their own words, because the
 * useful sentence is "you asked for X and this is not X" — not "this is CIF".
 */
export const DELIVERY_MISMATCH = {
  ar: { flag: 'ما يطابق طلبك — أنت طلبت التوصيل لين {ask}' },
  en: { flag: "Doesn't match what you asked for — you asked for delivery to {ask}" },
  zh: { flag: '与您的要求不符 — 您要求送到{ask}' },
};

/** What the trader asked for, phrased to slot into DELIVERY_MISMATCH. */
export const PREFERENCE_ASK_NOUN = {
  door:    { ar: 'بابك',            en: 'your door',      zh: '您的地址' },
  port:    { ar: 'الميناء السعودي', en: 'a Saudi port',   zh: '沙特港口' },
  factory: { ar: 'المصنع',          en: 'the factory',    zh: '工厂' },
};

/**
 * Explain one offer's price basis to a trader.
 *
 * POSITIVE LOOKUP, so a value invented later explains nothing rather than
 * explaining the wrong thing (golden rule 12). A fourth incoterm added to
 * PRICE_BASES without a matching entry here returns `null` — the row renders
 * exactly as it does today, with the code and no gloss. Silence is the safe
 * failure for copy that tells someone what a price covers.
 *
 * @returns {{gloss: string, full: string}|null}
 */
export function explainPriceBasis(basis, lang = 'en') {
  const key = norm(basis);
  const l = ['ar', 'en', 'zh'].includes(lang) ? lang : 'en';
  if (!key) return PRICE_BASIS_UNSPECIFIED[l];
  const entry = PRICE_BASIS_EXPLAINERS[key];
  return entry ? entry[l] : null;
}

/**
 * The one-line answer to "who delivers this to me?", for under the supplier's
 * name on the offer card. This is the sentence the owner approved on
 * 2026-09-21; do not reword it without them.
 *
 * @param {string} basis  offers.price_basis
 * @param {string} lang
 * @param {{city?: string}} opts  the request's ship_city, for DDP only
 */
export function deliveryLine(basis, lang = 'en', { city } = {}) {
  const l = ['ar', 'en', 'zh'].includes(lang) ? lang : 'en';
  const key = norm(basis);
  if (!key) return PRICE_BASIS_UNSPECIFIED[l].line;
  const entry = PRICE_BASIS_EXPLAINERS[key];
  // Positive lookup: an Incoterm added to the column but not to this file
  // renders nothing rather than the wrong promise.
  if (!entry) return '';
  // LOCALIZED, not raw (2026-09-21). ship_city stores the ENGLISH name in
  // every language, so an Arabic reader's line read «يوصّلها لين بابك في
  // Riyadh» -- one Latin word dropped into an Arabic sentence. cityLabel reads
  // the same KSA_CITIES list the picker wrote the value from, and falls back to
  // the raw string for anything it cannot map.
  const place = cityLabel(city, l);
  return entry[l].line.replace('{city}',
    place ? (l === 'ar' ? ` في ${place}` : l === 'zh' ? `（${place}）` : ` in ${place}`) : '');
}

/**
 * The flag text, or '' when there is nothing to flag. Takes the comparison
 * result rather than recomputing it, so deliveryMatch.js stays the only place
 * that decides what "short" means.
 */
export function deliveryMismatchText(deliveryPreference, lang = 'en') {
  const l = ['ar', 'en', 'zh'].includes(lang) ? lang : 'en';
  const noun = PREFERENCE_ASK_NOUN[String(deliveryPreference || '').trim().toLowerCase()];
  if (!noun) return '';
  return DELIVERY_MISMATCH[l].flag.replace('{ask}', noun[l]);
}
